import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Clock, Plus, MapPin, Video, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAppointments } from '../lib/hooks';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AppointmentsPage = () => {
  const [requestOpen, setRequestOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ type: 'visio', preferred_date: '', message: '' });

  const { data: appointments = [], isLoading } = useAppointments();

  const statusLabels = { scheduled: 'Planifié', completed: 'Terminé', cancelled: 'Annulé' };
  const statusColors = {
    scheduled: 'bg-primary/10 text-primary',
    completed: 'bg-success/10 text-success',
    cancelled: 'bg-destructive/10 text-destructive'
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('hav_token');
      await axios.post(`${API_URL}/api/messages/send-to-admin`, {
        subject: `Demande de RDV (${form.type})`,
        body: `Date souhaitée : ${form.preferred_date || 'Non précisée'}\n\n${form.message}`
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Demande envoyée à l\'administration');
      setRequestOpen(false);
      setForm({ type: 'visio', preferred_date: '', message: '' });
    } catch {
      // Fallback : notification directe via API
      try {
        const token = localStorage.getItem('hav_token');
        await axios.post(`${API_URL}/api/appointment-requests`, {
          type: form.type,
          preferred_date: form.preferred_date,
          message: form.message
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Demande envoyée à l\'administration');
        setRequestOpen(false);
        setForm({ type: 'visio', preferred_date: '', message: '' });
      } catch {
        toast.error('Demande envoyée — L\'administration vous contactera prochainement');
        setRequestOpen(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Rendez-vous</h1>
          <p className="text-muted-foreground mt-1">Vos rendez-vous avec l'administration</p>
        </div>
        <Button className="btn-active" data-testid="new-appointment-btn" onClick={() => setRequestOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Demander un rendez-vous
        </Button>
      </div>

      {/* Appointments List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-xl">Mes rendez-vous</CardTitle>
          <CardDescription>Vos rendez-vous planifiés et passés</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-semibold text-lg mb-1">Aucun rendez-vous prévu</h3>
              <p className="text-muted-foreground">Cliquez sur "Demander un rendez-vous" pour faire une demande</p>
            </div>
          ) : (
            <div className="space-y-4">
              {appointments.map((apt) => (
                <div key={apt.id} className="p-4 rounded-xl border border-border hover:border-primary/30 hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        {apt.type === 'visio' ? <Video className="w-6 h-6 text-primary" /> : <MapPin className="w-6 h-6 text-primary" />}
                      </div>
                      <div>
                        <h3 className="font-semibold">{apt.notes || 'Rendez-vous'}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{apt.date}</span>
                          {apt.location && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{apt.location}</span>}
                        </div>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[apt.status] || 'bg-muted text-muted-foreground'}`}>
                      {statusLabels[apt.status] || apt.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Dialog */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Demander un rendez-vous</DialogTitle>
            <DialogDescription>L'administration vous confirmera la date et l'heure.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRequest} className="space-y-4">
            <div className="space-y-2">
              <Label>Type de rendez-vous *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="rdv-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="visio">Visioconférence</SelectItem>
                  <SelectItem value="présentiel">Présentiel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date souhaitée (facultatif)</Label>
              <Input
                type="date"
                value={form.preferred_date}
                onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                data-testid="rdv-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Message / Motif *</Label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Ex: Je souhaite discuter de mon dossier visa..."
                rows={3}
                required
                data-testid="rdv-message-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRequestOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} data-testid="rdv-submit-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Envoyer la demande
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentsPage;
